export type SalesOrderQuotedSkuLine = {
  quoteId: number;
  quoteNumber: string;
  validThrough: string | null;
  sku: string;
  quantity: number;
  unitPrice: number;
};

function normalizeSku(sku: string): string {
  return sku.trim().toUpperCase();
}

/** True when valid-through is missing or the calendar day has not ended in local time. */
export function quoteLineStillValid(validThrough: string | null, now: Date = new Date()): boolean {
  if (!validThrough) return true;
  const expires = new Date(`${validThrough}T23:59:59`);
  return Number.isNaN(expires.getTime()) || expires.getTime() >= now.getTime();
}

export function activeSalesOrderQuoteLines(
  lines: readonly SalesOrderQuotedSkuLine[],
  now: Date = new Date(),
): SalesOrderQuotedSkuLine[] {
  return lines.filter((line) => quoteLineStillValid(line.validThrough, now));
}

/**
 * Choose the quoted unit price for a sales-order line.
 *
 * Among valid quote lines for the same SKU, use the highest quote quantity that is
 * still at or below the ordered quantity. If the order is below every break, or only
 * one quote line exists, use the lowest (or only) quantity break.
 */
export function selectSalesOrderQuotedSkuLine(
  lines: readonly SalesOrderQuotedSkuLine[],
  sku: string,
  orderedQuantity: number,
): SalesOrderQuotedSkuLine | null {
  const skuKey = normalizeSku(sku);
  if (skuKey === "") {
    return null;
  }

  const matches = lines.filter((line) => normalizeSku(line.sku) === skuKey && Number.isFinite(line.quantity));
  if (matches.length === 0) {
    return null;
  }
  if (matches.length === 1) {
    return matches[0] ?? null;
  }

  const ordered = Number.isFinite(orderedQuantity) ? orderedQuantity : 0;
  const atOrBelow = matches.filter((line) => line.quantity <= ordered);
  if (atOrBelow.length > 0) {
    return atOrBelow.reduce((best, line) => (line.quantity > best.quantity ? line : best));
  }
  return matches.reduce((best, line) => (line.quantity < best.quantity ? line : best));
}
